import * as React from "react";
import { useSetAtom } from "jotai";
import {
  type Language,
  type LocalizationContext,
} from "../api/localization";
import {
  fetchLocalizationModuleCached,
  getEnabledLanguagesCached,
  invalidateEnabledLanguagesCache,
  peekCachedEnabledLanguages,
} from "../api/localization-cache";
import { updateProfile } from "../api/users";
import { useAuth, useRole } from "../auth/hooks";
import { userAtom } from "../auth/atoms";
import { useWebSocket } from "../../hooks/useWebSocket";
import { LanguagePickerModal } from "../../../ui/components/LanguagePickerModal";
import { formatMessage, type TranslateValues, clearMessageFormatCache } from "./format";
import {
  BOOTSTRAP_EN,
  detectAppContext,
  getStoredLanguage,
  getStoredTimeZone,
  purgeLanguageModuleCaches,
  purgeLegacyModuleCaches,
  readCachedModule,
  removeCachedModule,
  setStoredLanguage,
  setStoredTimeZone,
  writeCachedModule,
} from "./storage";
import { detectBrowserTimeZone, matchBrowserLanguage } from "../locale";
import { ProApiClientError } from "../api/pro-api.client";
import * as fallbackStyles from "./I18nFallback.style.css";

export type { TranslateValues };

type I18nContextValue = {
  language: string;
  languages: Language[];
  ready: boolean;
  /** Plain-string translator (safe for placeholders, aria-labels, etc.). */
  t: (key: string, values?: TranslateValues) => string;
  /**
   * Translator that wraps fallback copy in a highlight span for super-admins
   * when the active language is missing the key.
   */
  tx: (key: string, values?: TranslateValues) => React.ReactNode;
  /** True when the active language has no definition for this key (using EN/bootstrap fallback). */
  isFallback: (key: string) => boolean;
  /** Super-admin + non-default language: show fallback highlighting. */
  showFallbackHighlight: boolean;
  setLanguage: (code: string) => Promise<void>;
  currentLanguage: Language | null;
  openLanguagePicker: () => void;
  closeLanguagePicker: () => void;
  languagePickerOpen: boolean;
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

// One-time per tab: drop outdated module caches before first read.
let didPurgeLegacyCaches = false;
function ensureLegacyCachesPurged() {
  if (didPurgeLegacyCaches) return;
  didPurgeLegacyCaches = true;
  purgeLegacyModuleCaches();
}

function pickFallbackLanguage(list: Language[], preferred?: string | null): string {
  const enabled = list.filter((l) => l.enabled);
  if (preferred) {
    const code = preferred.trim().toLowerCase();
    if (enabled.some((l) => l.code === code)) return code;
  }
  return (
    enabled.find((l) => l.isDefault)?.code ||
    enabled.find((l) => l.code === "en")?.code ||
    enabled[0]?.code ||
    "en"
  );
}

function readLocaleDefinitions(
  lang: string,
  appContext: LocalizationContext,
): Record<string, string> {
  ensureLegacyCachesPurged();
  const shared = readCachedModule(lang, "shared")?.definitions ?? {};
  const app = readCachedModule(lang, appContext)?.definitions ?? {};
  return { ...shared, ...app };
}

function mergeDefinitions(
  lang: string,
  appContext: LocalizationContext,
): Record<string, string> {
  ensureLegacyCachesPurged();
  const enShared = readCachedModule("en", "shared")?.definitions ?? {};
  const enApp = readCachedModule("en", appContext)?.definitions ?? {};
  const locale = readLocaleDefinitions(lang, appContext);
  return {
    ...BOOTSTRAP_EN,
    ...enShared,
    ...enApp,
    ...locale,
  };
}

function isNotFoundError(err: unknown): boolean {
  return err instanceof ProApiClientError && err.status === 404;
}

async function loadModule(
  lang: string,
  context: LocalizationContext,
  opts?: { blocking?: boolean },
): Promise<boolean> {
  ensureLegacyCachesPurged();
  const cached = readCachedModule(lang, context);

  const applyPayload = (payload: {
    unchanged: boolean;
    version: number;
    definitions?: Record<string, string>;
  }): boolean => {
    if (payload.unchanged && cached) return false;
    if (payload.definitions) {
      writeCachedModule(lang, context, {
        version: payload.version,
        definitions: payload.definitions,
      });
      return true;
    }
    return false;
  };

  if (cached && !opts?.blocking) {
    // Soft refresh: keep UI responsive, but always reconcile against server version.
    // Seed merges and admin edits bump version so clients pick up new keys.
    try {
      const payload = await fetchLocalizationModuleCached(
        context,
        lang,
        cached.version,
      );
      return applyPayload(payload);
    } catch (err) {
      if (isNotFoundError(err)) {
        removeCachedModule(lang, context);
      }
      return false;
    }
  }

  try {
    const payload = await fetchLocalizationModuleCached(
      context,
      lang,
      cached?.version,
    );
    return applyPayload(payload);
  } catch (err) {
    if (isNotFoundError(err)) {
      removeCachedModule(lang, context);
    }
    throw err;
  }
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const appContext = detectAppContext();
  const { user, accessToken, isAuthenticated } = useAuth();
  const setUser = useSetAtom(userAtom);
  const { hasRole } = useRole("super-admin");
  const isSuperAdmin = hasRole();
  const { subscribe, unsubscribe } = useWebSocket();

  const [language, setLanguageState] = React.useState(
    () => getStoredLanguage() || "en",
  );
  const [languages, setLanguages] = React.useState<Language[]>(
    () => peekCachedEnabledLanguages() ?? [],
  );
  const [definitions, setDefinitions] = React.useState<Record<string, string>>(
    () => mergeDefinitions(getStoredLanguage() || "en", appContext),
  );
  /** Keys defined on the active language modules only (no EN merge). */
  const [localeKeys, setLocaleKeys] = React.useState<ReadonlySet<string>>(
    () => new Set(Object.keys(readLocaleDefinitions(getStoredLanguage() || "en", appContext))),
  );
  const [ready, setReady] = React.useState(false);
  const [languagePickerOpen, setLanguagePickerOpen] = React.useState(false);
  const promotedRef = React.useRef(false);
  const promotingLangRef = React.useRef(false);
  const timezonePromotedRef = React.useRef(false);
  const promotingTimezoneRef = React.useRef(false);
  const lastSyncedProfileLangRef = React.useRef<string | null>(null);
  /** Language currently desired by auth/boot/picker — ignores stale ensureModules completions. */
  const desiredLanguageRef = React.useRef(getStoredLanguage() || "en");

  const refreshDefinitions = React.useCallback(
    (lang: string) => {
      clearMessageFormatCache();
      setDefinitions(mergeDefinitions(lang, appContext));
      setLocaleKeys(new Set(Object.keys(readLocaleDefinitions(lang, appContext))));
    },
    [appContext],
  );

  const refreshLanguages = React.useCallback(async (opts?: { force?: boolean }) => {
    try {
      const list = await getEnabledLanguagesCached(opts);
      setLanguages(list);
      return list;
    } catch {
      return null;
    }
  }, []);

  const ensureModules = React.useCallback(
    async (lang: string, blocking: boolean) => {
      const contexts: LocalizationContext[] = ["shared", appContext];
      const hasAnyCache =
        contexts.some((ctx) => readCachedModule(lang, ctx)) ||
        (lang !== "en" &&
          contexts.some((ctx) => readCachedModule("en", ctx)));

      const applyIfCurrent = () => {
        if (desiredLanguageRef.current !== lang) return;
        refreshDefinitions(lang);
      };

      // Serve cached/bootstrap copy immediately when available.
      if (hasAnyCache || !blocking) {
        applyIfCurrent();
      }

      const loadFor = async (targetLang: string) => {
        const results = await Promise.all(
          contexts.map(async (ctx) => {
            try {
              return await loadModule(targetLang, ctx, { blocking: true });
            } catch {
              return false;
            }
          }),
        );
        return results.some(Boolean);
      };

      try {
        await loadFor(lang);
        if (lang !== "en") {
          await loadFor("en");
        }
      } finally {
        // Re-merge only if this language is still desired (auth/picker may have switched).
        applyIfCurrent();
      }
    },
    [appContext, refreshDefinitions],
  );

  /** Switch to an enabled language; drop caches for orphan codes. */
  const adoptLanguage = React.useCallback(
    async (
      next: string,
      opts?: { syncProfile?: boolean; previous?: string | null },
    ) => {
      const code = next.trim().toLowerCase() || "en";
      const previous = opts?.previous ?? getStoredLanguage();
      if (previous && previous !== code && previous !== "en") {
        purgeLanguageModuleCaches(previous);
      }
      desiredLanguageRef.current = code;
      setStoredLanguage(code);
      setLanguageState(code);
      await ensureModules(code, true);
      if (opts?.syncProfile && isAuthenticated && accessToken) {
        try {
          const updated = await updateProfile(accessToken, {
            preferredLanguage: code,
          });
          lastSyncedProfileLangRef.current = code;
          // Apply PATCH response directly — avoids an extra GET /users/me.
          setUser(updated);
        } catch {
          // local preference already applied
        }
      }
    },
    [accessToken, ensureModules, isAuthenticated, setUser],
  );

  /** If current/stored lang is missing from enabled list, fall back to default/en. */
  const reconcileAvailableLanguage = React.useCallback(
    async (list: Language[], preferred?: string | null) => {
      const current = preferred ?? getStoredLanguage() ?? language;
      const resolved = pickFallbackLanguage(list, current);
      if (resolved !== current) {
        await adoptLanguage(resolved, {
          syncProfile: isAuthenticated && !!accessToken,
          previous: current,
        });
      }
      return resolved;
    },
    [adoptLanguage, accessToken, isAuthenticated, language],
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      // Seed local timezone from the browser when unset (before login).
      if (!getStoredTimeZone()) {
        const browserTz = detectBrowserTimeZone();
        if (browserTz) setStoredTimeZone(browserTz);
      }

      // Unblock consumers immediately — BOOTSTRAP_EN + any local module caches
      // already seed definitions. Network loads continue in the background.
      if (!cancelled) setReady(true);

      const list = await getEnabledLanguagesCached();
      if (!cancelled) setLanguages(list);

      if (cancelled) return;

      const enabledCodes = list.filter((l) => l.enabled).map((l) => l.code);
      // Resolve at write-time so a concurrent auth sync (profile language) wins
      // over a stale browser match from before fetch completed.
      const claimed = lastSyncedProfileLangRef.current;
      const storedNow = getStoredLanguage();
      const preferred =
        claimed ?? storedNow ?? matchBrowserLanguage(enabledCodes) ?? null;
      let resolved = pickFallbackLanguage(list, preferred);
      // Auth may have claimed the profile language between resolve and write.
      const claimedNow = lastSyncedProfileLangRef.current;
      if (claimedNow) {
        resolved = pickFallbackLanguage(list, claimedNow);
      } else if (
        storedNow &&
        resolved !== storedNow &&
        storedNow !== "en"
      ) {
        purgeLanguageModuleCaches(storedNow);
      }

      // If auth claimed a language during the awaits above, do not clobber it.
      const claimedFinal = lastSyncedProfileLangRef.current;
      if (claimedFinal) {
        resolved = pickFallbackLanguage(list, claimedFinal);
      }

      desiredLanguageRef.current = resolved;
      setStoredLanguage(resolved);
      setLanguageState(resolved);
      // Soft load — do not block UI for module network reconcile.
      await ensureModules(resolved, false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || !user || !accessToken) {
      // Allow the next login to re-apply profile language even if the code matches.
      lastSyncedProfileLangRef.current = null;
      return;
    }

    const profileLang =
      user.preferredLanguage != null && String(user.preferredLanguage).trim()
        ? String(user.preferredLanguage).trim().toLowerCase()
        : null;
    const stored = getStoredLanguage();

    (async () => {
      const list =
        languages.length > 0 ? languages : (await refreshLanguages()) ?? [];

      if (profileLang) {
        if (lastSyncedProfileLangRef.current === profileLang) {
          // Still verify the language remains enabled (e.g. deleted while signed in).
          const stillOk = list.some((l) => l.code === profileLang && l.enabled);
          if (!stillOk) {
            const resolved = pickFallbackLanguage(list, profileLang);
            lastSyncedProfileLangRef.current = resolved;
            await adoptLanguage(resolved, {
              syncProfile: true,
              previous: profileLang,
            });
          } else {
            // Heal any stale catalog left by a race with an earlier language load.
            desiredLanguageRef.current = profileLang;
            setStoredLanguage(profileLang);
            setLanguageState(profileLang);
            refreshDefinitions(profileLang);
          }
        } else {
          const resolved = pickFallbackLanguage(list, profileLang);
          lastSyncedProfileLangRef.current = resolved;
          if (resolved !== profileLang) {
            await adoptLanguage(resolved, {
              syncProfile: true,
              previous: profileLang,
            });
          } else {
            desiredLanguageRef.current = resolved;
            setStoredLanguage(resolved);
            setLanguageState(resolved);
            await ensureModules(resolved, false);
          }
        }
      }

      // Promote locale prefs in one PATCH (+ one refresh) when profile lacks them.
      const profileTz =
        user.preferredTimezone != null && String(user.preferredTimezone).trim()
          ? String(user.preferredTimezone).trim()
          : null;

      const needsLangPromote =
        !profileLang && !promotedRef.current && !promotingLangRef.current;
      const needsTzPromote =
        !profileTz &&
        !timezonePromotedRef.current &&
        !promotingTimezoneRef.current;

      if (needsLangPromote || needsTzPromote) {
        if (needsLangPromote) promotingLangRef.current = true;
        if (needsTzPromote) promotingTimezoneRef.current = true;

        const enabledCodes = list.filter((l) => l.enabled).map((l) => l.code);
        const preferredLang =
          stored ?? matchBrowserLanguage(enabledCodes) ?? null;
        const resolvedLang = pickFallbackLanguage(list, preferredLang);
        const tz = getStoredTimeZone() || detectBrowserTimeZone();

        const patch: {
          preferredLanguage?: string;
          preferredTimezone?: string;
        } = {};
        if (needsLangPromote) patch.preferredLanguage = resolvedLang;
        if (needsTzPromote && tz) patch.preferredTimezone = tz;

        try {
          if (needsLangPromote) {
            if (stored && resolvedLang !== stored) {
              await adoptLanguage(resolvedLang, {
                syncProfile: false,
                previous: stored,
              });
            } else {
              desiredLanguageRef.current = resolvedLang;
              setStoredLanguage(resolvedLang);
              setLanguageState(resolvedLang);
              await ensureModules(resolvedLang, false);
            }
            lastSyncedProfileLangRef.current = resolvedLang;
            promotedRef.current = true;
          }
          if (needsTzPromote && tz) {
            setStoredTimeZone(tz);
            timezonePromotedRef.current = true;
          }
          if (Object.keys(patch).length > 0) {
            const updated = await updateProfile(accessToken, patch);
            setUser(updated);
          }
        } catch {
          if (needsLangPromote) promotedRef.current = false;
          if (needsTzPromote) timezonePromotedRef.current = false;
        } finally {
          promotingLangRef.current = false;
          promotingTimezoneRef.current = false;
        }
      } else if (profileTz) {
        setStoredTimeZone(profileTz);
        timezonePromotedRef.current = true;
      }
    })();
  }, [
    isAuthenticated,
    user,
    user?.preferredLanguage,
    user?.preferredTimezone,
    accessToken,
    languages,
    ensureModules,
    refreshLanguages,
    refreshDefinitions,
    adoptLanguage,
    setUser,
  ]);

  React.useEffect(() => {
    const handler = (msg: {
      channel?: string;
      action?: string;
      payload?: unknown;
    }) => {
      const channel = msg.channel ?? "";
      const parts = channel.split(":");
      if (parts[0] !== "entity" || parts[1] !== "localization") return;

      // Language lifecycle: entity:localization:languages
      if (parts[2] === "languages") {
        const payload =
          msg.payload && typeof msg.payload === "object"
            ? (msg.payload as { code?: string; enabled?: boolean })
            : null;
        const code = payload?.code?.trim().toLowerCase();
        void (async () => {
          invalidateEnabledLanguagesCache();
          const list = (await refreshLanguages({ force: true })) ?? [];
          if (
            code &&
            (msg.action === "deleted" || payload?.enabled === false) &&
            code !== "en"
          ) {
            purgeLanguageModuleCaches(code);
          }
          await reconcileAvailableLanguage(list, getStoredLanguage());
        })();
        return;
      }

      const lang = parts[2];
      const context = parts[3] as LocalizationContext | undefined;
      if (!lang || !context) return;
      if (lang !== language && lang !== "en") return;
      void loadModule(lang, context, { blocking: false }).then(() => {
        refreshDefinitions(language);
      });
    };
    subscribe(["entity:localization:*"], handler);
    return () => unsubscribe(["entity:localization:*"], handler);
  }, [
    language,
    subscribe,
    unsubscribe,
    refreshDefinitions,
    refreshLanguages,
    reconcileAvailableLanguage,
  ]);

  const setLanguage = React.useCallback(
    async (code: string) => {
      await adoptLanguage(code, { syncProfile: true });
      setLanguagePickerOpen(false);
    },
    [adoptLanguage],
  );

  const openLanguagePicker = React.useCallback(() => {
    setLanguagePickerOpen(true);
    void refreshLanguages();
  }, [refreshLanguages]);

  const defaultLanguageCode =
    languages.find((l) => l.isDefault)?.code ?? "en";

  const showFallbackHighlight =
    ready &&
    isSuperAdmin &&
    language !== defaultLanguageCode &&
    language !== "en";

  const isFallback = React.useCallback(
    (key: string) => {
      if (language === "en" || language === defaultLanguageCode) return false;
      // Present on the active language module → not a fallback.
      if (localeKeys.has(key)) return false;
      // Missing entirely or served from EN/bootstrap → fallback.
      return true;
    },
    [language, defaultLanguageCode, localeKeys],
  );

  const t = React.useCallback(
    (key: string, values?: TranslateValues) => {
      // `||`, not `??`: a definition saved as an empty string (e.g. "Add
      // untranslated keys" run before a default existed) must still fall
      // back to BOOTSTRAP_EN rather than rendering blank.
      const message = definitions[key] || BOOTSTRAP_EN[key] || key;
      return formatMessage(language, key, message, values);
    },
    [definitions, language],
  );

  const tx = React.useCallback(
    (key: string, values?: TranslateValues): React.ReactNode => {
      const text = t(key, values);
      if (showFallbackHighlight && isFallback(key)) {
        return (
          <span
            className={fallbackStyles.fallbackHighlight}
            data-i18n-fallback={key}
            title={`Missing ${language} translation — showing fallback (${key})`}
          >
            {text}
          </span>
        );
      }
      return text;
    },
    [t, showFallbackHighlight, isFallback, language],
  );

  const currentLanguage =
    languages.find((l) => l.code === language) ??
    languages.find((l) => l.isDefault) ??
    null;

  const value = React.useMemo<I18nContextValue>(
    () => ({
      language,
      languages,
      ready,
      t,
      tx,
      isFallback,
      showFallbackHighlight,
      setLanguage,
      currentLanguage,
      openLanguagePicker,
      closeLanguagePicker: () => setLanguagePickerOpen(false),
      languagePickerOpen,
    }),
    [
      language,
      languages,
      ready,
      t,
      tx,
      isFallback,
      showFallbackHighlight,
      setLanguage,
      currentLanguage,
      openLanguagePicker,
      languagePickerOpen,
    ],
  );

  return (
    <I18nContext.Provider value={value}>
      {children}
      <LanguagePickerModal
        open={languagePickerOpen}
        setOpen={(open: boolean) => {
          if (!open) setLanguagePickerOpen(false);
        }}
        languages={languages}
        currentCode={language}
        title={t("shared.languagePicker.title")}
        closeLabel={t("shared.languagePicker.close")}
        onSelect={setLanguage}
      />
    </I18nContext.Provider>
  );
}

export function StaticI18nProvider({ children }: { children: React.ReactNode }) {
  const value = React.useMemo<I18nContextValue>(() => {
    const t = (key: string, values?: TranslateValues) =>
      formatMessage("en", key, BOOTSTRAP_EN[key] || key, values);
    return {
      language: "en",
      languages: [],
      ready: true,
      t,
      tx: t,
      isFallback: () => false,
      showFallbackHighlight: false,
      setLanguage: async () => {},
      currentLanguage: null,
      openLanguagePicker: () => {},
      closeLanguagePicker: () => {},
      languagePickerOpen: false,
    };
  }, []);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = React.useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
}

/** Safe hook for optional usage outside provider (returns bootstrap t). */
export function useT(): (key: string, values?: TranslateValues) => string {
  const ctx = React.useContext(I18nContext);
  if (!ctx) {
    return (key, values) =>
      formatMessage("en", key, BOOTSTRAP_EN[key] ?? key, values);
  }
  return ctx.t;
}

/**
 * Localized text node. Prefer this (or `tx()`) for visible copy so super-admins
 * see fallback highlighting when a translation is missing.
 */
export function T({
  id,
  values,
}: {
  id: string;
  values?: TranslateValues;
}) {
  const { tx } = useI18n();
  return <>{tx(id, values)}</>;
}
